<template>
  <div class="cm">
    <WelcomeHeader />

    <!-- Masthead -->
    <header class="cm-mast">
      <div class="cm-mast-scan" aria-hidden="true"></div>
      <div class="cm-wrap">
        <nav class="cm-crumb">
          <a @click="goHub">সাম্প্রতিক সমাচার</a>
          <span class="cm-crumb-sep">/</span>
          <span class="cm-crumb-cur">{{ issue.label }}</span>
        </nav>
        <p class="cm-eyebrow">
          <span class="cm-eyebrow-dash" aria-hidden="true"></span>বিসিএস প্রস্তুতি · মাসিক সংকলন
        </p>
        <h1 class="cm-title">সাম্প্রতিক সমাচার</h1>
        <div class="cm-rule" aria-hidden="true">
          <span class="cm-rule-bar"></span>
          <span class="cm-rule-diamond"></span>
          <span class="cm-rule-fade"></span>
        </div>
        <p class="cm-issue">
          <span>{{ issue.label }} সংখ্যা</span>
          <span class="cm-issue-dot"></span>
          <span><b>{{ toBengali(issue.count) }}</b>টি নির্বাচিত তথ্য</span>
          <span class="cm-issue-dot"></span>
          <span>{{ toBengali(issue.categories.length) }}টি বিভাগ</span>
        </p>
      </div>
    </header>

    <!-- Sticky index / controls -->
    <div class="cm-controls">
      <div class="cm-wrap">
        <div class="cm-search">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M21 21l-4.3-4.3M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16z" stroke="#9a978c" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <input
            v-model="query"
            type="search"
            aria-label="প্রশ্ন খুঁজুন"
            placeholder="প্রশ্ন বা উত্তর খুঁজুন…"
          />
        </div>

        <div class="cm-index">
          <button
            class="cm-tab"
            :class="{ active: activeCategory === 'all' }"
            @click="activeCategory = 'all'"
          >
            সব <span>{{ toBengali(filteredTotal) }}</span>
          </button>
          <button
            v-for="c in issue.categories"
            :key="c.key"
            class="cm-tab"
            :class="{ active: activeCategory === c.key }"
            @click="activeCategory = c.key"
          >
            {{ c.name }} <span>{{ toBengali(countFor(c.key)) }}</span>
          </button>

          <button class="cm-master" @click="toggleAll">
            {{ allRevealed ? "উত্তর লুকান" : "সব উত্তর দেখুন" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Body -->
    <main class="cm-body">
      <div class="cm-wrap">
        <template v-for="group in visibleGroups" :key="group.key">
          <section v-if="group.items.length" class="cm-section">
            <div class="cm-section-head">
              <span class="cm-section-no">{{ toBengali(pad(group.no)) }}</span>
              <h2 class="cm-section-name">{{ group.name }}</h2>
              <span class="cm-section-count">{{ toBengali(group.items.length) }}টি</span>
            </div>

            <article
              v-for="item in group.items"
              :key="item.id"
              class="cm-entry"
              :class="{ open: isOpen(item.id) }"
            >
              <button
                class="cm-q"
                :aria-expanded="isOpen(item.id) ? 'true' : 'false'"
                @click="toggle(item.id)"
              >
                <span class="cm-serial">{{ toBengali(item.n) }}</span>
                <span class="cm-q-text">{{ item.q }}</span>
                <span class="cm-toggle" aria-hidden="true">{{ isOpen(item.id) ? "−" : "+" }}</span>
              </button>
              <div class="cm-a-wrap">
                <div class="cm-a">
                  <span class="cm-a-label">উত্তর</span>
                  <p class="cm-a-text">{{ item.a }}</p>
                </div>
              </div>
            </article>
          </section>
        </template>

        <p v-if="filteredTotal === 0" class="cm-empty">
          “{{ query }}” — এর সাথে মিল থাকা কোনো প্রশ্ন পাওয়া যায়নি।
        </p>
      </div>
    </main>

    <WelcomeFooter :hide-download="true" />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from "vue";
import router from "@/router";
import WelcomeHeader from "@/components/WelcomePages/WelcomeHeader.vue";
import WelcomeFooter from "@/components/WelcomePages/WelcomeFooter.vue";
import { URLs } from "@/static";
import { getIssue } from "@/components/WelcomePages/currentAffairsData";

export default {
  components: { WelcomeHeader, WelcomeFooter },
  setup() {
    const issue = getIssue("june-2026");

    // প্রতিটি বিভাগে ক্রমিক নম্বর, আর প্রতিটি আইটেমে বিভাগভিত্তিক ক্রমিক ও ইউনিক id যোগ করা হয়।
    const categories = issue.categories.map((c, i) => ({
      ...c,
      no: i + 1,
      items: c.items.map((it, idx) => ({ ...it, n: idx + 1, id: `${c.key}-${idx}` })),
    }));

    const activeCategory = ref("all");
    const query = ref("");
    const revealed = reactive(new Set());
    const allRevealed = ref(false);

    const toBengali = (n) => {
      const bn = ["০", "১", "২", "৩", "৪", "৫", "৬", "৭", "৮", "৯"];
      return String(n).split("").map((c) => bn[parseInt(c)] ?? c).join("");
    };
    const pad = (n) => String(n).padStart(2, "0");

    const matches = (it) => {
      const q = query.value.trim().toLowerCase();
      if (!q) return true;
      return it.q.toLowerCase().includes(q) || it.a.toLowerCase().includes(q);
    };

    // সার্চ প্রয়োগ করা বিভাগসমূহ (ট্যাব-নিরপেক্ষ) — গণনার জন্য।
    const searchedGroups = computed(() =>
      categories.map((c) => ({ ...c, items: c.items.filter(matches) }))
    );

    const countFor = (key) => {
      const g = searchedGroups.value.find((c) => c.key === key);
      return g ? g.items.length : 0;
    };
    const filteredTotal = computed(() =>
      searchedGroups.value.reduce((sum, c) => sum + c.items.length, 0)
    );

    // দৃশ্যমান গ্রুপ = সার্চ + সক্রিয় ট্যাব ফিল্টার।
    const visibleGroups = computed(() =>
      searchedGroups.value.filter(
        (c) => activeCategory.value === "all" || activeCategory.value === c.key
      )
    );

    const isOpen = (id) => revealed.has(id);
    const syncAllFlag = () => {
      const total = categories.reduce((s, c) => s + c.items.length, 0);
      allRevealed.value = revealed.size >= total;
    };
    const toggle = (id) => {
      if (revealed.has(id)) revealed.delete(id);
      else revealed.add(id);
      syncAllFlag();
    };
    const toggleAll = () => {
      if (allRevealed.value) {
        revealed.clear();
        allRevealed.value = false;
      } else {
        categories.forEach((c) => c.items.forEach((it) => revealed.add(it.id)));
        allRevealed.value = true;
      }
    };

    const goHub = () => router.push({ name: URLs.ROUTE_CURRENT_AFFAIRS_NAME });

    const setMeta = (attrs) => {
      const selector = attrs.property ? `meta[property="${attrs.property}"]` : `meta[name="${attrs.name}"]`;
      let el = document.querySelector(selector);
      if (!el) { el = document.createElement("meta"); document.head.appendChild(el); }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    };
    const addFont = (href) => {
      if (document.querySelector(`link[href="${href}"]`)) return;
      const l = document.createElement("link");
      l.rel = "stylesheet";
      l.href = href;
      document.head.appendChild(l);
    };

    onMounted(() => {
      window.scrollTo(0, 0);
      // ডিজাইন অনুযায়ী প্রথম প্রশ্নটি খোলা অবস্থায় দেখানো হয়।
      const first = categories[0]?.items[0];
      if (first) revealed.add(first.id);
      addFont("https://fonts.googleapis.com/css2?family=Hind+Siliguri:wght@400;500;600;700&family=Noto+Serif+Bengali:wght@500;600;700&display=swap");
      document.title = `${issue.label} সাম্প্রতিক সমাচার — কারেন্ট অ্যাফেয়ার্স | বিসিএস প্রস্তুতি`;
      setMeta({ name: "description", content: `${issue.label} মাসের ${toBengali(issue.count)}টি বাছাইকৃত সাম্প্রতিক প্রশ্নোত্তর — বাংলাদেশ, আন্তর্জাতিক, বিজ্ঞান ও প্রযুক্তি এবং ক্রীড়াঙ্গন।` });
      setMeta({ name: "robots", content: "index, follow" });
      setMeta({ property: "og:title", content: `${issue.label} সাম্প্রতিক সমাচার — বিসিএস প্রস্তুতি` });
      setMeta({ property: "og:description", content: `${issue.label} মাসের ${toBengali(issue.count)}টি সাম্প্রতিক প্রশ্নোত্তর।` });
      setMeta({ property: "og:url", content: `https://www.bcsprostuti.com${issue.route}` });
      setMeta({ property: "og:type", content: "article" });
      let canonical = document.querySelector('link[rel="canonical"]');
      if (!canonical) { canonical = document.createElement("link"); canonical.rel = "canonical"; document.head.appendChild(canonical); }
      canonical.href = `https://www.bcsprostuti.com${issue.route}`;
    });

    return {
      issue, activeCategory, query, allRevealed,
      toBengali, pad, countFor, filteredTotal, visibleGroups,
      isOpen, toggle, toggleAll, goHub,
    };
  },
};
</script>

<style scoped>
.cm {
  --ink: #191B47;
  --ink2: #40445f;
  --muted: #8a8ea3;
  --paper: #f6f4ee;
  --card: #fffdf9;
  --line: #e7e3d8;
  --line-soft: #efece3;
  --brass: #b08a3e;
  --brass-lt: #d9b978;
  --blue: #0f6fd0;
  font-family: "Hind Siliguri", "Segoe UI", system-ui, sans-serif;
  color: var(--ink2);
}
.cm-wrap { position: relative; max-width: 820px; margin: 0 auto; padding: 0 22px; text-align: left; }

/* ---- Masthead ---- */
.cm-mast {
  position: relative; overflow: hidden;
  background:
    radial-gradient(130% 130% at 100% 0%, rgba(176,138,62,0.20) 0%, rgba(176,138,62,0) 46%),
    radial-gradient(90% 90% at 0% 100%, rgba(15,111,208,0.18) 0%, rgba(15,111,208,0) 55%),
    linear-gradient(158deg, #101230 0%, #1c2150 62%, #191b47 100%);
  color: #fff;
  padding: 52px 0 46px;
  border-bottom: 1px solid rgba(176,138,62,0.4);
}
.cm-mast-scan {
  position: absolute; inset: 0; pointer-events: none;
  background-image: linear-gradient(rgba(255,255,255,0.028) 1px, transparent 1px);
  background-size: 100% 7px;
}
.cm-crumb { display: flex; justify-content: flex-start; align-items: center; gap: 9px; font-size: 0.8rem; color: #96a0bd; margin-bottom: 26px; }
.cm-crumb a { color: #c3d2ef; cursor: pointer; transition: color .15s; }
.cm-crumb a:hover { color: #fff; }
.cm-crumb-sep { color: #5c6288; }
.cm-crumb-cur { color: #dbe3f2; }
.cm-eyebrow { display: flex; align-items: center; gap: 10px; font-size: 0.7rem; font-weight: 600; letter-spacing: 0.26em; text-transform: uppercase; color: var(--brass-lt); margin: 0 0 14px; }
.cm-eyebrow-dash { width: 18px; height: 1px; background: var(--brass); flex: none; }
.cm-title {
  font-family: "Noto Serif Bengali", Georgia, serif;
  font-weight: 700; letter-spacing: -0.015em; line-height: 1.1;
  font-size: clamp(2rem, 7vw, 3.6rem);
  margin: 0; color: #fff;
}
.cm-rule { display: flex; align-items: center; gap: 10px; margin: 24px 0 20px; }
.cm-rule-bar { width: 52px; height: 2px; background: linear-gradient(90deg, var(--brass) 0%, var(--brass-lt) 100%); flex: none; }
.cm-rule-diamond { width: 5px; height: 5px; transform: rotate(45deg); background: var(--brass-lt); flex: none; }
.cm-rule-fade { flex: 1; max-width: 180px; height: 1px; background: linear-gradient(90deg, rgba(217,185,120,0.5), rgba(217,185,120,0)); }
.cm-issue { display: flex; align-items: center; flex-wrap: wrap; gap: 8px 14px; font-size: 0.9rem; color: #c4cbe0; margin: 0; }
.cm-issue b { color: var(--brass-lt); font-weight: 700; font-family: "Noto Serif Bengali", serif; font-size: 1rem; }
.cm-issue-dot { width: 4px; height: 4px; border-radius: 50%; background: rgba(217,185,120,0.55); flex: none; }

/* ---- Sticky index ---- */
.cm-controls {
  position: sticky; top: 0; z-index: 20;
  background: rgba(246,244,238,0.9); backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--line); padding: 15px 0;
}
.cm-search {
  display: flex; align-items: center; gap: 10px;
  background: #fff; border: 1px solid var(--line); border-radius: 11px;
  padding: 11px 15px; margin-bottom: 13px;
  transition: border-color .15s, box-shadow .15s;
}
.cm-search:focus-within { border-color: #cfc8b6; box-shadow: 0 0 0 3px rgba(176,138,62,0.12); }
.cm-search input { border: none; background: transparent; outline: none; width: 100%; font-size: 0.95rem; color: var(--ink); font-family: inherit; }
.cm-search input::placeholder { color: #a7a495; }

.cm-index { display: flex; flex-wrap: nowrap; gap: 8px; overflow-x: auto; padding-bottom: 2px; align-items: center; scrollbar-width: none; }
.cm-index::-webkit-scrollbar { display: none; }
.cm-tab {
  flex: 0 0 auto; display: inline-flex; align-items: center; gap: 7px; white-space: nowrap;
  font-size: 0.82rem; font-weight: 600; color: #6a6f83; background: #fff;
  border: 1px solid var(--line); border-radius: 999px; padding: 7px 14px; cursor: pointer;
  font-family: inherit; transition: all .15s ease;
}
.cm-tab:hover { border-color: #cfc8b6; color: var(--ink); }
.cm-tab span { font-size: 0.72rem; font-weight: 700; color: #9a9787; }
.cm-tab.active { color: #fff; background: var(--ink); border-color: var(--ink); }
.cm-tab.active span { color: var(--brass-lt); }
.cm-master {
  flex: 0 0 auto; margin-left: auto; white-space: nowrap;
  font-size: 0.8rem; font-weight: 700; color: var(--ink);
  background: transparent; border: 1px solid var(--ink); border-radius: 999px;
  padding: 7px 15px; cursor: pointer; font-family: inherit; transition: all .15s;
}
.cm-master:hover { background: var(--ink); color: #fff; }

/* ---- Body ---- */
.cm-body { background: var(--paper); padding: 34px 0 84px; min-height: 40vh; }
.cm-section { margin-bottom: 40px; }
.cm-section:last-child { margin-bottom: 0; }
.cm-section-head {
  display: flex; align-items: baseline; flex-wrap: wrap; gap: 6px 12px;
  margin-bottom: 18px; padding-bottom: 14px; border-bottom: 1px solid var(--line);
}
.cm-section-no {
  font-family: "Noto Serif Bengali", Georgia, serif; font-weight: 700;
  font-size: 2.3rem; line-height: 0.9; color: rgba(176,138,62,0.4); flex: 0 0 auto;
}
.cm-section-name {
  font-family: "Noto Serif Bengali", Georgia, serif; font-weight: 700;
  font-size: 1.24rem; color: var(--ink); margin: 0; flex: 0 0 auto; letter-spacing: -0.01em;
}
.cm-section-count { font-size: 0.69rem; font-weight: 700; letter-spacing: 0.12em; color: #a09c8c; flex: 0 0 auto; }

/* ---- Entry ---- */
.cm-entry {
  background: var(--card); border: 1px solid var(--line); border-radius: 13px;
  margin-bottom: 10px; overflow: hidden;
  box-shadow: 0 1px 1px rgba(25,27,71,0.03);
  transition: border-color .18s ease, box-shadow .18s ease;
}
.cm-entry:hover { border-color: #ddd6c6; box-shadow: 0 6px 18px rgba(25,27,71,0.06); }
.cm-entry.open { border-color: #d7cfba; box-shadow: 0 6px 20px rgba(25,27,71,0.07); }

.cm-q {
  display: flex; align-items: flex-start; gap: 14px; width: 100%; text-align: left;
  background: transparent; border: none; cursor: pointer; padding: 17px 20px; font-family: inherit;
}
.cm-serial {
  flex: 0 0 auto; width: 27px; height: 27px; border-radius: 50%;
  background: rgba(176,138,62,0.08); border: 1px solid rgba(176,138,62,0.28);
  display: flex; align-items: center; justify-content: center; margin-top: 1px;
  font-family: "Noto Serif Bengali", Georgia, serif; font-size: 0.78rem; font-weight: 700; color: var(--brass);
  transition: background .18s ease, border-color .18s ease, color .18s ease;
}
.cm-entry.open .cm-serial { background: var(--ink); border-color: var(--ink); color: var(--brass-lt); }
.cm-q-text { flex: 1; font-size: 0.98rem; font-weight: 600; color: #2a3049; line-height: 1.62; }

.cm-toggle {
  flex: 0 0 auto; width: 20px; height: 20px; border-radius: 50%; margin-top: 4px;
  background: rgba(176,138,62,0.10); display: flex; align-items: center; justify-content: center;
  color: var(--brass); font-size: 0.9rem; font-weight: 700; line-height: 1;
  transition: background .18s ease, color .18s ease;
}
.cm-entry.open .cm-toggle { background: var(--ink); color: var(--card); }

/* ---- Answer ---- */
.cm-a-wrap { max-height: 0; overflow: hidden; transition: max-height .3s ease; }
.cm-entry.open .cm-a-wrap { max-height: 640px; }
.cm-a {
  text-align: left; margin: 0 20px 18px; padding: 15px 18px;
  background: #f4f1e8; border: 1px solid var(--line-soft); border-left: 2px solid var(--brass-lt);
  border-radius: 4px 11px 11px 4px;
}
.cm-a-label { display: inline-block; font-size: 0.64rem; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: var(--blue); margin-bottom: 7px; }
.cm-a-text { margin: 0; font-size: 0.96rem; line-height: 1.78; color: #3a3f57; }

.cm-empty { text-align: center; color: var(--muted); padding: 54px 0; font-size: 0.98rem; font-style: italic; }

@media (max-width: 600px) {
  .cm-mast { padding: 38px 0 32px; }
  .cm-crumb { margin-bottom: 18px; }
  .cm-rule { margin: 20px 0 16px; }
  .cm-section-no { font-size: 1.9rem; }
  .cm-section-name { font-size: 1.1rem; }
  .cm-q { padding: 15px 15px; gap: 11px; }
  .cm-a { margin: 0 15px 15px; }
}

@media (prefers-reduced-motion: reduce) {
  .cm-a-wrap, .cm-serial, .cm-toggle, .cm-entry { transition: none; }
}
</style>
